import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authenticatedFetch, isAuthenticated } from '../utils/auth';

const AddressContext = createContext();

export const useAddresses = () => {
  const context = useContext(AddressContext);
  if (!context) {
    throw new Error('useAddresses must be used within an AddressProvider');
  }
  return context;
};

export const AddressProvider = ({ children }) => {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);

  // Fetch addresses from API
  const fetchAddressesFromAPI = useCallback(async () => {
    // Only fetch addresses if user is authenticated
    if (!isAuthenticated()) {
      console.log('User not authenticated, skipping address fetch');
      return [];
    }

    setLoading(true);
    setError(null);

    try {
      const response = await authenticatedFetch('/addresses');
      if (response.ok) {
        const addressesData = await response.json();
        setAddresses(addressesData);
        setLastFetched(Date.now());
        return addressesData;
      } else {
        throw new Error('Failed to fetch addresses');
      }
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Main fetch function - always fetches fresh data
  const fetchAddresses = useCallback(async () => {
    return await fetchAddressesFromAPI();
  }, [fetchAddressesFromAPI]);

  // Force refresh (same as fetchAddresses now)
  const refreshAddresses = useCallback(async () => {
    return await fetchAddressesFromAPI();
  }, [fetchAddressesFromAPI]);

  // Initialize addresses on mount - only if authenticated
  useEffect(() => {
    if (isAuthenticated()) {
      fetchAddresses().catch(error => {
        console.warn('Failed to fetch addresses on mount:', error);
        // Don't throw the error, just log it
      });
    }
  }, [fetchAddresses]);

  const value = {
    addresses,
    loading,
    error,
    lastFetched,
    fetchAddresses,
    refreshAddresses
  };

  return (
    <AddressContext.Provider value={value}>
      {children}
    </AddressContext.Provider>
  );
};
